/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 获客舱 · 商机 API(契约:apps/获客仓重构-契约-0917.md §4)
 * ═══════════════════════════════════════════════════════════════════════════
 * ★ 字段名逐字照抄契约的 camelCase,**本仓不做映射层**。改字段名必须先改契约文件。
 * ★ 后端(gscq-api 批A/批B)此刻还没上线:dev 模式下任何一口失败都会退到
 *   `./acqFixtures` 的本地桩(**只在 `import.meta.env.MODE === "development"`**,
 *   vitest 的 MODE 是 `test`、构建产物是 `production`,两处都拿不到桩)。
 *   端点一通,桩自动让位 —— 不需要回来删代码。
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { api } from "@/shared/api";
import { productService } from "@/entities/product";
import { logger } from "@/shared/lib/logger";

const BASE = "/acq";

// ── 契约 §2 状态机 ─────────────────────────────────────────────────────────
export type AcqState =
  | "DISCOVERED"
  | "QUALIFIED"
  | "READY_TO_CONTACT"
  | "FIRST_TOUCH"
  | "ENGAGED"
  | "NURTURE"
  | "QUALIFIED_OPPORTUNITY"
  | "WON"
  | "LOST";

/**
 * 契约 §4.1:**只给一个**,不给动作清单。
 * ★ 0917 v2(契约 §12.6)加了 `ANSWER`(知乎提问 → 去回答)与 `DM`(微博/抖音评论 → 去私信)。
 *   这两个出口**不需要联系方式** —— 它们是内容线的落点,不是电话线的。
 */
export type AcqSuggestedAction = "CALL" | "WECHAT" | "EMAIL" | "VISIT" | "WATCH" | "ANSWER" | "DM";

/** 契约 §4.2 五按钮。 */
export type AcqFeedbackCode = "won" | "lost" | "wrong_person" | "bad_phone" | "follow_up";

/**
 * 意向类型(契约 §14.3)——「这个人想干什么」,精判输出的一格。
 *
 * ★ 与 `suggestedAction`(该做什么)**不是一回事**:同样是 PRICE(问价),
 *   知乎上的出口是回答、客服会话里的出口是回一句话 —— 意向定话术,动作定出口。
 * ★ 取值域**开放**(与 `source` / `archetype` 同则):卡上的字段声明成 `string`,
 *   查不到中文面的原样露出 code(见 `intentTypeLabel`)—— 后端加了新类型当场看得见。
 *   这个 union 只用来钉**筛子里给哪几档**,不用来断言"服务端只会给这七个"。
 */
export type AcqIntentType =
  | "PURCHASE"
  | "PRICE"
  | "QUALIFICATION"
  | "COMPLIANCE"
  | "EXPANSION"
  | "PRODUCT"
  | "OTHER";

/**
 * 一条事实。★`evidenceId` + `sourceUrl` 缺一不可 —— 缺了前端**整条不渲染**
 * (契约 §4.1 硬要求①:点不开原文的"事实"就是编的引用)。
 */
export interface AcqFact {
  label: string;
  text: string;
  evidenceId: string;
  sourceUrl: string;
  provider?: string | null;
}

/** AI 推断。与 `facts` 分两个字段,前端分两块排版(契约 §4.1 硬要求②)。 */
export interface AcqInference {
  text: string;
  suggestedAction: AcqSuggestedAction;
  opener?: string | null;
}

/** 契约 §10 四维。 */
export interface AcqScores {
  fit: number;
  intent: number;
  timing: number;
  reach: number;
}

/**
 * 联系方式。
 * ★ 0917 v2(契约 §12.4):**`masked` 恒 false** —— 脱敏那套(`AcqContactMasking`)退役了。
 *   新口径是「没找到就是没有」:`phone` 要么是真号码,要么是空。前端**不再有 masked 分支**,
 *   字段保留只为兼容还没跟上的后端版本,读到 true 也不做别的事。
 */
export interface AcqContact {
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  address?: string | null;
  masked: boolean;
  /**
   * 这串号码是**哪儿来的**(契约 §14.4)。缺席 = 不知道来源,面上写「来源未知」,
   * **不许回落到「公告原文」** —— 编一个出处比不给出处坏得多(§14.7)。
   */
  provenance?: AcqContactProvenance | null;
  /** 主号码之外的联系人(1025 启客拓客:一家常解锁回来好几个人)。别的线没有 = 空 / 缺席。 */
  others?: AcqContactLine[] | null;
}

/** 一个联系人(1025)。tags 是中文:决策人 / 关键人 / 疑似空号。 */
export interface AcqContactLine {
  value: string;
  type: "mobile" | "landline" | "email";
  name?: string | null;
  duty?: string | null;
  tags: string[];
}

/**
 * 联系方式的来源与把握(契约 §14.4 `subject_detail.contact.provenance`)。
 *
 * ★ 三条线三种来源,可信度差一个量级:公告原文抄下来的(0.95)、自家客服会话里对方
 *   自己留的(1.0)、公网搜出来的(0.8~0.9)。销售在打之前该知道自己手上是哪一种。
 * ★ `costMicro` 是平台侧上游成本,**不进对客面**(与 `spentMicro` 同则);
 *   留字段是为了 `/provider-stats` 那条路,不是给卡用的。
 * ★ 全部选填:批次三之前的后端整块不发这个对象。
 */
export interface AcqContactProvenance {
  /** `announcement`(公告原文)/ `cs_session`(客服会话)/ `zhipu`(公网检索)… 取值域开放。 */
  provider?: string | null;
  operation?: string | null;
  costMicro?: number | null;
  /** 0~1。号码逐字命中正文 0.8,多页一致 0.9,公告抄录 0.95,自家会话 1.0。 */
  confidence?: number | null;
  verifiedAt?: string | null;
  sourceUrl?: string | null;
}

/**
 * 接手(免费)之后 AI 给的产物(契约 §12.4 / §12.5)。
 *
 * ★ 这是 v2 的形态核心:主按钮不再是「花钱看电话」,而是**免费先给你一份能直接用的东西**。
 *   - `answer_draft` 回答稿(ANSWER)、`opener` 开场白(CALL/EMAIL/WECHAT/VISIT)、`outreach` 私信话术(DM)
 *   - `targetUrl` = 该去哪儿用它(问题页 / 作者主页 / 第一条证据的原文)
 * ★ KB 为空时 ai 侧出通用稿,并在 `text` 首行标 `[通用稿]` —— 面上要把这件事说出来,
 *   不能让用户以为这是照着他的资料写的。
 */
export interface AcqDeliverable {
  /**
   * ★ 0917 批次三加 `reply_draft`(契约 §14.6):第一方线的产物是**接着这段会话往下说的
   *   那一句**,不是开场白 —— 对方已经在跟你说话了,再来一句「您好,我们看到贵司…」
   *   是把一个正在问价的人当陌生人重新认识一遍。
   */
  kind: "answer_draft" | "opener" | "outreach" | "reply_draft";
  text: string;
  targetUrl?: string | null;
}

/** 产物首行的「这是通用稿」标记(ai 侧逐字写 `[通用稿]`,契约 §12.5)。 */
export const GENERIC_DRAFT_MARK = "[通用稿]";

export function isGenericDraft(d?: AcqDeliverable | null): boolean {
  return !!d && d.text.trimStart().startsWith(GENERIC_DRAFT_MARK);
}

/** 契约 §4.1 `AcqOpportunityCard`,逐字。 */
/**
 * 跑一轮采集的回执(POST /acq/discover/run,body `{type}` 必填)。
 *
 * ★ 0917 v2(契约 §12.4):**发现按种类收费**,所以回执必须说出
 *   `type`(跑的是哪一种)、`charged`(这轮到底扣没扣)、`credits`(扣了多少)。
 * ★★ `inserted + updated == 0` 时后端**不 settle**(契约 §12.10)⇒ `charged=false`。
 *   前端据此说「这轮没发现,没扣分」—— 不许反过来,那是让用户为我们的失败付钱。
 * ★ `spentMicro` 是平台侧上游成本,只给运营看,**不是对客价**(两条账并存是有意的)。
 */
export interface AcqDiscoverRunResult {
  inserted: number;
  updated: number;
  skippedNoRef: number;
  rawCount: number;
  judgedCount: number;
  spentMicro: number;
  sourcesUsed: string[];
  signalDictMissing: boolean;
  type: string;
  charged: boolean;
  credits: number;
  /**
   * local-only: B (backend schema gap) — 0923 起 api 透传 ai 的「每条信号为什么没成卡」计数。
   * 键见 `explainZeroRun`;老后端不带这一格 → 按缺席处理。
   */
  rejectBreakdown?: Record<string, number>;
  /**
   * 0925:本轮漏斗(ai `judge.build_funnel` → api `AcqDiscoverFunnel` 原样透传)。
   * 0 张时据此分三种说法(见 `explainZeroRunByFunnel`);老后端 / 第一方线给 null 或不带 → 回落现文案。
   */
  funnel?: AcqDiscoverFunnel | null;
  /**
   * 1027 归属分权:`updated` 里有几张是**同事名下**的卡 —— 扫到了,但卡归原主,我在列表里看不到。
   * 只报个数不给内容;老后端不带 → 按 0。
   */
  heldByColleague?: number;
}

/** 一轮「发现一次」的状态(0927 异步化)。`RUNNING` 之外都是终态。 */
export type AcqDiscoverRunStatus = "RUNNING" | "SUCCESS" | "FAILED";

/**
 * `POST /acq/discover/run` 的**即时**回执(0927 异步化)。
 *
 * ★ 发起成功就是 `RUNNING`:结果不在这里,要拿 `runId` 去 `GET /acq/discover/runs/{id}` 轮询
 *   (`acqService.discoverRun`)。发不起来是 4xx —— 种类 / 产品 / 余额三道闸仍在后端请求线程上同步过,
 *   所以「余额不足」还是当场知道,不是四秒后轮询回来的一句话。
 * ★ `runId` 同时是 settle 幂等键里的动作号:页面上这一轮追得回台账那一行。
 */
export interface AcqDiscoverRunStarted {
  runId: string;
  type: string;
  productCode?: string | null;
  status: AcqDiscoverRunStatus;
  startedAt: string;
}

/**
 * 一轮的一次读数(`GET /acq/discover/runs/{id}`;也挂在目录卡的 `lastRun` 上)。
 *
 * ★ `elapsedMs` 由服务端算(RUNNING 是「到现在」,终态是「开跑到结束」):「已等 m:ss」**别拿 `startedAt`
 *   减本机时间** —— 两台机器的钟不必对得上。页面刷新后接上一轮在跑的,起点 = `Date.now() - elapsedMs`。
 * ★ `result` 只在 `SUCCESS` 有,形状与同步时代的回执**完全一样**:「扣没扣 / 0 张为什么」那套文案原样复用。
 * ★ `error` 只在 `FAILED` 有,是后端写给用户的一句人话,原样露出。
 */
export interface AcqDiscoverRunView {
  runId: string;
  type: string;
  productCode?: string | null;
  status: AcqDiscoverRunStatus;
  startedAt: string;
  finishedAt?: string | null;
  elapsedMs?: number | null;
  result?: AcqDiscoverRunResult | null;
  error?: string | null;
}

/** 每轮发现的漏斗统计(契约 0925 `funnel`,三仓同一套键名)。 */
export interface AcqDiscoverFunnel {
  /** 各条线抓回来的原始条数 */
  fetched: number;
  /** 真正过了精判的条数(判调用失败的不算) */
  judged: number;
  /** 本轮出的卡数 */
  hits: number;
  /** 判成同行 / 服务商软文的条数 */
  rejectedAsPeer: number;
  /** 其余原因落选的条数(不含判调用失败) */
  rejectedOther: number;
}

/**
 * 「这家最近」时间线上的一条(契约 §14.2 `recentSignals`)。
 *
 * ★ `label` 由服务端给(它那边有同一份 source → 中文的表);**取不到就用本仓的
 *   `sourceLabel(source)` 兜底** —— 两处各有一份表不是重复,是这一格在后端没跟上时
 *   仍然读得出是哪条线。
 */
export interface AcqRecentSignal {
  source: string;
  label?: string | null;
  happenedAt?: string | null;
  sourceUrl?: string | null;
}

/**
 * 卡上的一个产品(契约 §19.2 卡 DTO `products: [{code,name}]`)。
 *
 * ★ `name` 为 **null = 这个产品下架了**(产品树里查不到名字了),卡照旧要露出来 ——
 *   把它整条丢掉,用户会以为这家只对得上一个产品;而面上写「未知产品」等于把
 *   唯一能查的线索(那个码)抹掉。所以 null 时**显示 code、灰着**(与 `nameOf` 同则)。
 */
export interface AcqCardProduct {
  code: string;
  name?: string | null;
}

/** 线索卡「企业画像」(1015,后端 `CompanyDto`)。格子都可空,空的不显示、不补「未知」。 */
export interface AcqCompany {
  name?: string | null;
  creditCode?: string | null;
  legalPerson?: string | null;
  regCapital?: string | null;
  /** `yyyy-MM-dd` */
  establishDate?: string | null;
  regStatus?: string | null;
  industry?: string | null;
  region?: string | null;
  website?: string | null;
  email?: string | null;
}

// ── 1015 企业库找客户(P2,后端 `AcqEntDtos`)───────────────────────────────

export interface AcqEntIndustryItem {
  code: string;
  name: string;
}

/** 国标 GB/T 4754—2017 一个门类,下挂大类。 */
export interface AcqEntIndustrySection {
  code: string;
  name: string;
  items: AcqEntIndustryItem[];
}

/** 按客户画像预填:行业码、地区名原样(前端转地区码)、排除项原样(只提示)、企业名关键词(1020 起;老后端没有)。 */
export interface AcqEntPrefill {
  industryCodes: string[];
  regionNames: string[];
  exclusions: string[];
  keywords?: string[];
}

export interface AcqEntOptions {
  /** 能不能按条件筛(只有天眼查能);false ⇒ 页面显示「暂未开通」。 */
  searchEnabled: boolean;
  suggestEnabled: boolean;
  /** 每页积分;null = 价表故障,写「按页计费」。 */
  priceCredits: number | null;
  dailyLimit: number;
  usedToday: number;
  industries: AcqEntIndustrySection[];
  tags: string[];
  prefill: AcqEntPrefill;
}

export interface AcqEntSearchRequest {
  industryCode?: string | null;
  /** 天眼查地区码 = "00" + 6 位行政区划码。 */
  areaCode?: string | null;
  keyword?: string | null;
  tagName?: string | null;
  page?: number;
  /** 上游不支持,只在当页过滤。 */
  capitalMinWan?: number | null;
  estYearsMin?: number | null;
}

export interface AcqEntCompany {
  name: string;
  creditCode?: string | null;
  legalPerson?: string | null;
  regCapital?: string | null;
  establishDate?: string | null;
  regStatus?: string | null;
  region?: string | null;
  /** 线索库里已经有这家(入库会跳过)。 */
  inLeads: boolean;
}

export interface AcqEntSearchResult {
  searchId: string;
  page: number;
  total?: number | null;
  items: AcqEntCompany[];
  /** 这一页实扣(0 条 / 24h 内重看 = 0)。 */
  chargedCredits: number;
  /** 被注销 / 吊销与当页过滤去掉的条数。 */
  filteredOut: number;
}

export interface AcqEntSuggestion {
  name: string;
  creditCode?: string | null;
  legalPerson?: string | null;
  regStatus?: string | null;
}

export interface AcqEntImportResult {
  created: number;
  existed: number;
  opportunityIds: string[];
  /** 1027:`existed` 里有几家已在同事名下 —— 它们的 id **不在** `opportunityIds` 里。老后端不带 → 0。 */
  heldByColleague?: number;
}

/** 批量补全里一条的结果(后端 `AcqBatchUnlockService.Item`)。 */
export interface AcqBatchUnlockItem {
  id: number;
  status: "pending" | "found" | "not_found" | "already" | "insufficient" | "failed";
  credits: number;
}

/** 批量补全进度(后端 `AcqBatchUnlockService.Progress`)。 */
export interface AcqBatchUnlockProgress {
  taskId: string;
  total: number;
  done: number;
  found: number;
  notFound: number;
  failed: number;
  chargedCredits: number;
  finished: boolean;
  stoppedReason?: string | null;
  items: AcqBatchUnlockItem[];
}

export interface AcqOpportunityCard {
  id: string;
  subjectName: string;
  subjectType: string;
  source: string;
  region?: string | null;
  industryCode?: string | null;
  priorityScore: number;
  scores?: AcqScores | null;
  state: AcqState;
  happenedAt?: string | null;
  detectedAt?: string | null;
  freshnessDays?: number | null;
  /** 这件事过期的时刻(投标截止 / 证书到期);没有就 null。 */
  deadlineAt?: string | null;
  /** 离过期还有几天;**负数 = 已过期**(线索库能翻到,今日商机页已排掉);没死线就 null。 */
  daysLeft?: number | null;
  matchedProductCode?: string | null;
  matchedProductName?: string | null;
  /**
   * 0918 批次八(契约 §19):**这家能卖哪几个产品**,主产品在前(`[0].code === matchedProductCode`)。
   *
   * ★★ 为什么要这一格:上一版一张卡只有**一个** `matched_product_code`,而精判在同一条信号上
   *   总选更贴的那个 —— 于是「销帮AI智脑」「400 电话」这两个页签**恒 0 商机**(老板原话)。
   *   一家刚拿资质的制造企业本来就同时是 GEO + 增值推广 + AI 智脑三个产品的客户。
   * ★ **选填**:批次八之前的后端只发 `matchedProductCode/Name` 那一对。缺席 / 空数组 ⇒
   *   消费侧退回那一对拼成的单枚 chip(见 `cardProducts`)—— 不编、不空。
   * ★ `matchedProductCode` 保留 = 主产品:页签的"在看哪条线"、我的生意的归属都还读它。
   */
  products?: AcqCardProduct[] | null;
  /**
   * 分派给谁(契约 §13.3 `assignee_id`)。**null / 缺席 = 没人认领**。
   * ★ 缺席与 null 在这里是**同一件事**:批次二之前的后端根本不发这两个字段,
   *   那时整页都还没有"分派"这回事 —— 不是"分派给了一个我不知道的人"。
   */
  assigneeId?: number | null;
  /** 分派给谁的显示名。有 id 没名字时面上写「已分派」,**不拿 id 当名字显示**。 */
  assigneeName?: string | null;
  // ── 0917 批次三(契约 §14.2 / §14.3)。**全部选填**,理由同批次二那组 ──────────
  /**
   * 意向类型(契约 §14.3)。卡头一个 pill,今日与线索库多一个筛子。
   * ★ 声明成 `string` 不是 `AcqIntentType`:取值域开放,查不到中文面的原样露出 code。
   */
  intentType?: string | null;
  /**
   * 这条**为什么在这个状态**(契约 §14.3 `state_reason`)。
   * ★ 每次改 state 的地方后端都必须写一句(AI 判定 / 谁接手 / 谁点了什么 / 谁反馈了什么),
   *   所以它回答的是线索库里最常被问的那个问题:「上周那条后来怎么了」。
   * ★ 缺席 = 那一次改状态**没记原因**(旧数据,或后端漏写)。面上就别摆 hover ——
   *   给一个空 tooltip 比没有 tooltip 更像坏了。
   */
  stateReason?: string | null;
  /**
   * 这张卡参与过的**信号源集合**(契约 §14.2 `sources_json`)。跨源合卡之后一家公司
   * 只有一张卡,而「招标 + 招聘 + 知乎有人问」三件事同时成立正是它值得做的理由。
   * ★ 只有一个源时与 `source` 同值 ⇒ 面上照旧显示那一个源的名字,不写「1 个来源」。
   */
  sources?: string[];
  /**
   * 「这家最近」时间线(契约 §14.2):按证据行的 `signal_source` 分组,每源取最新一条,
   * 最多 5 条。
   * ★ 每条都带 `sourceUrl` 才点得开 —— 与事实块同一条纪律(点不开出处的不渲染成链接)。
   */
  recentSignals?: AcqRecentSignal[];
  facts: AcqFact[];
  inference?: AcqInference | null;
  unlocked: boolean;
  contact?: AcqContact | null;
  feedbackCode?: AcqFeedbackCode | null;
  /** 补全一条联系方式要扣多少积分。服务端给,**前端不硬编码 50**。 */
  unlockCredits?: number | null;
  /**
   * 1015 企业库:补全价格区间的上沿。接了企业库时企业库命中那一档更贵(120),
   * 卡面写「50–120 积分,查到才扣」;没接时与 `unlockCredits` 相同、卡面只显示一个数。
   * **选填**:老后端不发 ⇒ 按 `unlockCredits` 单价显示。
   */
  unlockCreditsMax?: number | null;
  /** 1015 企业库:企业工商画像(补全时企业库查到才有)。缺席 / null ⇒ 不渲染那一块。 */
  company?: AcqCompany | null;
  /** 接手过没有(契约 §12.4)。false ⇒ 主按钮是「接手(免费)」;true ⇒ 产物面板展开。 */
  taken: boolean;
  /** 接手的产物。只有 `taken=true` 才会有。 */
  deliverable?: AcqDeliverable | null /**
   * 1009 评论线(契约 §4):卡面小标签,如「竞品评论区」「作者已回复」。
   * **选填**:老后端不发 / 空数组 ⇒ 不渲染。
   */;
  tags?: string[];
}

/** 后端 `AcqOpportunityPage(long total, int page, int pageSize, List<AcqOpportunityCard> items)`。 */
export interface AcqOpportunityPage {
  total: number;
  page: number;
  pageSize: number;
  items: AcqOpportunityCard[];
}

/**
 * 今日商机(后端 `AcqTodayView(int cap, List<AcqOpportunityCard> items)`)。
 *
 * ★ `cap` = 当天订阅的 `dailyCap` 回显。有它,页面才能说「今天给你挑了 3 条(每天最多 10 条)」——
 *   而不是干巴巴列三张卡让人猜"是只有这三条,还是被截断了"。
 */
export interface AcqTodayView {
  cap: number;
  items: AcqOpportunityCard[];
  /**
   * 分页(契约 §13.4 `GET /today?page=&size=&mine=&productCode=`,返回 `{cap,total,page,size,items}`)。
   * ★ 三个都**选填**:批次二之前的后端只发 `{cap, items}`。缺了就按"只有这一页"渲染 ——
   *   拿一个猜的 total 去画分页条,会让人点到一页空的然后以为单子丢了。
   * ★ 这里是 `size` 不是 `pageSize`:线索库那口叫 pageSize,今日这口契约写的是 size。
   *   两个名字不是笔误也不该在前端"统一"掉 —— 改名就对不上后端了。
   */
  total?: number;
  page?: number;
  size?: number;
  /**
   * 0917 批次五(契约 §16.2):产品页签的数据。**选填** —— 缺席时这一页照旧渲染,
   * 页签那一排改从产品树拿名字、不报数(报一个猜的数比不报坏:用户会照着它去翻)。
   * ★ 这里读的是 `todayCount` 那一格,不是 `openCount`:今日页的页签说的是"今天有几条"。
   */
  productSummary?: AcqProductSummary[] | null;
}

/** 今日商机的筛子(契约 §13.4)。`mine` = 只看分派给我的;`productCode` = 按产品筛。 */
export interface AcqTodayQuery {
  page?: number;
  size?: number;
  mine?: boolean;
  /**
   * 1027 归属分权:`pool` = 只看公共池(没人认的)。不传 = 我范围里的全部 + 公共池。
   * 范围本身由后端按登录人钳(员工 = 自己的 + 公共池,经理 = 分管子树 + 公共池),这一格只收窄。
   */
  scope?: "pool";
  productCode?: string;
  /** 0917 批次三(契约 §14.3):按意向类型筛 —— 「今天只想接问价的」。 */
  intentType?: string;
}

/**
 * 补全联系方式的结果(后端 `UnlockResult(charged, credits, found, card)`,契约 §12.4)。
 *
 * ★★ `charged` 区分「这次真扣了」与「早就补过、幂等没再扣」。
 *   重复点同一条**不许再弹一次「已扣 N 积分」** —— 那是在吓唬一个其实没花钱的用户。
 * ★★ `found` 是 v2 新加的那一格,也是最要紧的一格:公网上**根本查不到**联系方式时
 *   `found=false && charged=false` —— 没找到不收钱(契约 §12.10)。
 *   没有这一格的话,「查不到」和「查到了但没扣(幂等)」在前端长得一模一样,
 *   于是我们只能二选一:要么对一次失败说「已补全」,要么对一次幂等说「没找到」。
 * ★ `credits` 是**实扣数**,面上显示它,永远不硬编 50。
 */
export interface AcqUnlockResult {
  charged: boolean;
  credits: number;
  found: boolean;
  card: AcqOpportunityCard;
}

/**
 * 重判补绑的回执(契约 §14.9 `POST /acq/opportunities/rejudge`)。
 *
 * ★★ **不扣积分、不 settle**:这是一次系统性补账,不是用户动作 —— 老卡的
 *   `matched_product_code` / `intent_type` 是我们上一版没采到的,不该让租户为它付钱。
 * ★ 三个数说的是三件事,面上至少要说前两个:`scanned` 扫了几张(让人知道范围)、
 *   `updated` 真绑上几张(这一下值不值)、`unmatched` 模型也判不出的那几张
 *   (它们不是失败,是"这几张确实和你的产品树对不上")。
 */
export interface AcqRejudgeResult {
  scanned: number;
  updated: number;
  unmatched: number;
}

/** 接手(POST /acq/opportunities/{id}/take)。**免费**,返回带 `deliverable` 的整张卡。 */
export interface AcqTakeResult {
  card: AcqOpportunityCard;
}

export interface AcqOpportunityQuery {
  page?: number;
  pageSize?: number;
  state?: AcqState | "";
  source?: string;
  feedbackCode?: AcqFeedbackCode | "";
  /**
   * 分派筛子(契约 §13.4 `/opportunities?assigneeId=`)。
   * ★★ `UNASSIGNED_ASSIGNEE_ID`(0)= **未分派**。契约没写"未分派"怎么传,这是本仓选的表示法,
   *   后端要把 `assigneeId=0` 读成 `assignee_id IS NULL`(联调第一条要对的东西)。
   *   选 0 不选一个 `unassigned=true` 的新参数,是因为后端**忽略不认识的参数不会报错** ——
   *   那样「未分派」会静默返回全部行,用户看到的是一页错的名单而没有任何一处报错;
   *   而 0 若没被特判,返回的是**空**列表 —— 错得看得见。
   */
  assigneeId?: number | null;
  /** 按产品筛(`matched_product_code`)。 */
  productCode?: string;
  /** 0917 批次三(契约 §14.3):按意向类型筛。 */
  intentType?: string;
}

/** 「未分派」在 `assigneeId` 上的表示法。见 `AcqOpportunityQuery.assigneeId` 的注释。 */
export const UNASSIGNED_ASSIGNEE_ID = 0;

/**
 * 一个租户成员。0917 批次四起**只有桩在用**(桩要把 `assigneeId` 翻成人名) ——
 * 真机的人名来自系统的 `MemberTreePicker`(组织树 + `/tenant/members`),这一舱不再自己拉一份。
 */
export interface AcqTenantUser {
  id: number;
  displayName: string;
  role?: string;
  roleLabel?: string;
  enabled?: boolean;
}

/** 产品筛子 / 「我卖什么」用的一个产品节点(`/tenant/products` 的子集,契约 §13.6)。 */
export interface AcqProduct {
  nodeCode: string;
  name: string;
  summary?: string | null;
}

// ── 发现目录(契约 §12.1~§12.4) ────────────────────────────────────────────
//
// v2 的形态:用户不是「配一张订阅表」,而是**先挑一个地方手动找一次**(按次收费),
// 觉得有用再把它变成「订阅自动找」。所以目录是这一舱的入口,不是设置页。

/** 推荐档(契约 §12.3 `level`)。`NA` = 还没推断过 / 推不出来(KB 为空是常态)。 */
export type AcqPrefLevel = "P0" | "P1" | "P2" | "NA";

/** 种类状态。**只有两个值**:能跑的 / 还没通的(契约 §12.2)。 */
export type AcqTypeStatus = "ACTIVE" | "COMING_SOON";

/** 这一种在哪儿跑(契约 §12.2 + §14.6 新增 `api`)。 */
export type AcqRunVia = "ai" | "link" | "api";

/**
 * 读 `runVia` 的**唯一入口**。
 *
 * ★★ 为什么不能直接 `item.runVia === "link"`:契约写的是小写 `ai | link`,而 api 那边
 *   发的是 `RunVia.name()` —— **大写**的 `AI` / `LINK`(`AcqCatalogItemAssembler` 第 118 行)。
 *   直接比小写字面量的后果是静默的:地图门店 / 行业趋势这两张 link 卡会长出一个
 *   「发现一次 · 按次计费」的按钮(它们根本没有 SKU),而「订阅自动找」开关在**所有**
 *   种类上一起消失 —— 桩里全小写,所以测试与样式桩两边都是绿的。
 *   本仓这里**吃两种大小写**,并在报告里把这处对不上交给联调去统一。
 * ★ 认不出来的值回落到 `ai`(= 露「发现一次」):与加 `api` 之前的默认分支一致 ——
 *   多一个能点的按钮,好过一个新种类在页面上整个失踪。
 */
export function runViaOf(item: Pick<AcqCatalogItem, "runVia">): AcqRunVia {
  const v = String(item.runVia ?? "").toLowerCase();
  return v === "link" ? "link" : v === "api" ? "api" : "ai";
}

/** 订阅节奏。WEEKLY = 每周一跑一次。 */
export type AcqCadence = "DAILY" | "WEEKLY";

/**
 * 目录里的一种「发现种类」= 平台定义(name/archetype/status/runVia)+ 本租户偏好
 * (level/why/keywords/subscribed/cadence/lastRun*)+ 价(priceCredits)。
 *
 * ★ `priceCredits` 服务端从 PricingService 现读,**读不到就是 `null`**,
 *   前端一律写「按次计费」——(契约 §12.4 原话:永不编数)。
 * ★ `runVia="link"` 的种类(地图门店 / 行业趋势)没有「发现一次」按钮,
 *   它们的出口是 `linkTo` 指的那个已有页面。
 * ★ `status="COMING_SOON"` 的**照显推荐档**但不露发现按钮(契约 §12.10):
 *   产品要展示广度,但不能展示假能力。
 */
export interface AcqCatalogItem {
  type: string;
  name: string;
  archetype: string;
  archetypeLabel: string;
  description: string;
  status: AcqTypeStatus;
  level: AcqPrefLevel;
  why?: string | null;
  keywords: string[];
  regions: string[];
  /** `user` = 用户手改过的词,**永不被重新推断覆盖**(契约 §12.10)。 */
  keywordsSource: "ai" | "user";
  /**
   * 1009 评论线(契约 §2):**评论原型**去哪类视频下找人的话题词(搜视频用)。
   * 选填:老后端不发;非评论原型给空数组。卡面对评论原型优先显示它,空则回落 `keywords`。
   */
  topicWords?: string[];
  /** 1009 评论线(契约 §2):客户在这类视频下常问的短句,**只用于排序提示**。选填。 */
  intentPatterns?: string[];
  /**
   * 1011 竞品账号 / 指定内容。**只有抖音、小红书评论线有**(其余种类 / 老后端为 null 或缺席 ⇒
   * 编辑框不摆那两栏)。
   */
  peerTargets?: AcqPeerTargets | null;
  subscribed: boolean;
  cadence: AcqCadence;
  priceCredits?: number | null;
  lastRunAt?: string | null;
  lastResultCount?: number | null;
  /**
   * 这张卡**最近一轮**「发现一次」(0927 异步化)。**选填**:老后端不发、从没跑过、记录过期都是缺席。
   * ★ `status === "RUNNING"` ⇒ 页面加载后**接上轮询**(`useDiscoverRun.resume`),按钮照样「正在找…」——
   *   刷新页面不该让一轮正在花钱的采集在面上消失。
   * ★ 终态时卡上多一行:0 张为什么(`result.funnel`)/ 没跑完为什么(`error`)。
   *   `lastRunAt / lastResultCount` 那对老字段照旧由后端出货后写,两处不互相替代。
   */
  lastRun?: AcqDiscoverRunView | null;
  /**
   * 这一种**在哪儿跑**:`ai` 走 ai 的 discover;`link` 的出口是既有页面;
   * `api`(0917 批次三,契约 §14.6)= 全在 api 内做、不经 ai —— 目前只有第一方意向
   * (自家客服会话)。`api` 与 `ai` 在**面上是一回事**:都有「发现一次」。
   * ★★ 读它一律经 `runViaOf()`,不要写 `item.runVia === "link"` —— 见那个函数的注释。
   */
  runVia: AcqRunVia | string;
  linkTo?: string | null;

  // ── 0917 批次二(契约 §13.4)。**全部选填** ──────────────────────────────
  // ★ 为什么选填:后端批次二与本仓并行开工,一个还没跟上的 api 会整组不发这几个字段。
  //   声明成必填不会让那种情况报错(运行时没人校验),只会让**读的地方以为一定有** ——
  //   于是 `item.products.map(...)` 在真机上炸成白屏。选填 + 消费侧兜底才是诚实的形状。
  /**
   * 档位是谁定的(契约 §13.3 `level_source`)。
   * `user` = 用户自己「设为主推 / 设为可试」过 ⇒ **重新推断不覆盖档位**(与关键词同一条规则)。
   */
  levelSource?: "ai" | "user";
  /** 用户点过「不看这个」。折进「更多」尾部灰显,不从目录里消失 —— 消失了他找不回来。 */
  hidden?: boolean;
  /** 这一种主要服务哪几个产品(`node_code`)。 */
  products?: string[];
  /**
   * 关键词按产品分组 `{node_code: [词]}`(AI 给的)。
   * ★ `keywords` 仍是**扁平并集,采集只读它**(契约 §13.3 列注释)——
   *   分组只是面上的一种呈现,用户改词改的还是那份扁平表(改完进「通用」组)。
   */
  keywordsByProduct?: Record<string, string[]>;
  /**
   * 0921:**通用词** = 扁平表里没被任何产品认领的词(用户在卡上手加的词都落在这里)。
   * 后端算好发下来,口径与采集取词同一处(`AcqKeywordSelection`)。选填:老后端不发。
   */
  genericKeywords?: string[];
  /**
   * 0921:每个产品**实际会拿去搜**的词 `{node_code: [通用词…, 这个产品自己的词…]}`。
   * ★ 产品页签下卡面读它,不再读 `keywordsByProduct[code]` 原样 —— 后者只有 AI 给的词,
   *   用户手加的词只进扁平表,于是「卡上看不见、搜索也没用上」(0921 用户反馈原话)。
   * 选填:老后端不发,读的地方退回 `keywordsByProduct`。
   */
  searchKeywordsByProduct?: Record<string, string[]>;
  /** 学习环 v1(契约 §13.1):同行业其他租户在这一种上的反馈聚合。 */
  peerFeedback?: AcqPeerFeedback | null;

  // ── 0917 批次三(契约 §14.3)────────────────────────────────────────────────
  /**
   * 这一种**多半会捞回什么意向**(卡上「多为:问价 / 采购」)。
   * ★ 它回答的是目录页最贵的那个问题:「花 180 积分跑一次,回来的是什么东西」——
   *   名字和一句话说的是"去哪儿找",这一行说的是"找回来的人想干嘛"。
   * ★ 缺席 ⇒ 整行不出现,**不写「暂无」**:一行"暂无"占着位置却不给信息。
   */
  intentTypes?: string[];

  // ── 0917 批次五(契约 §16.1)────────────────────────────────────────────
  /**
   * 引用了这一条线的场景 id(`AcqCatalogView.scenes[].id`)。
   * ★ 卡上「场景:…」那一行、场景 chip 点选时的高亮、以及**按产品重算分区**
   *   (契约 §16.7)三件事全读它。缺席 = 这一版后端还没跟上 ⇒ 场景行不出现、
   *   按产品分区时这条线落「更多」—— 不假装它被某个场景引用过。
   */
  sceneIds?: string[] | null;

  // ── 0918 批次六(契约 §17.2 / §17.3.1)。**全部选填**:api 那辆车与本仓并行开工 ──
  /**
   * 用户**按产品**手调过的档(`{node_code: "P0"|"P1"|"P2"|"NA"}`,后端 `product_levels_json`)。
   *
   * ★★ 按产品看的时候 **`productLevels` 优先于场景重算**(契约 §17.2 末句):
   *   用户对"这条线对我这个产品有没有用"的判断,比 AI 从 KB 推的场景更硬。
   *   没有这一格才回到「场景 → 词组 → 更多」那套。
   * ★ 它与全局的 `level` / `levelSource` **是两格**:全局档是"不分产品时的建议",
   *   这一格是"在这个产品下你说了算"。混成一格的后果是用户在 A 产品下点「不看」,
   *   B 产品下那条线也跟着消失。
   */
  productLevels?: Record<string, AcqPrefLevel> | null;
  /**
   * 「订阅自动找」订的是哪几个产品(契约 §17.3.1 `subscribed_products_json`)。
   * 空 / 缺席 = 沿用全部词(即 `subscribed` 那个全局开关)。
   * ★ 缺席时按产品看的那个开关**退回读 `subscribed`** —— 不假装"这个产品没订",
   *   那会让一个已经在每天跑的订阅在页面上显示成关着的。
   */
  subscribedProducts?: string[] | null;
}

/**
 * 同行业反馈(契约 §13.4 `peerFeedback`)。
 * ★ 契约表格里这一项写成 `{won, wrongPerson, bad_phone, shareBp}` —— 那个 `bad_phone` 是笔误
 *   (同一份 DTO 里其余三个都是 camelCase)。本仓按 camelCase `badPhone` 读,
 *   api 侧发的必须是同一个拼法,否则这一格恒 undefined 而页面什么也不报。
 * ★ `shareBp` = 万分比的采集份额(10000 = 100%),目录同档内按它降序。
 */
export interface AcqPeerFeedback {
  won: number;
  wrongPerson: number;
  badPhone: number;
  shareBp: number;
}

/**
 * 目录页的整份数据(契约 §12.4 `AcqCatalogView`)。
 *
 * ★ `inferredFrom` 要摆在页面顶上:推荐是从**知识库 + 行业包 + 画像**推的,
 *   三样都没有时推出来的全是 `NA` —— 那时页面必须说清「因为没料」,
 *   而不是显示一页灰卡让人以为功能坏了(实测 33 个租户绝大多数 KB=0)。
 */
export interface AcqCatalogView {
  recommendedAt?: string | null;
  inferredFrom: { kbDocs: number; industryPack: boolean; icp: boolean };
  pushEnabled: boolean;
  pushHour: number;
  items: AcqCatalogItem[];
  /**
   * 今日商机每天最多给几条(契约 §13.1:`clamp(activeSeats × 5, 10, 100)`,服务端按席位算)。
   * ★ 选填,而且**前端永不自己算** —— 席位口径在 `EntitlementUsageService` 那边,
   *   这里若照 `cap/5` 反推席位数,在 clamp 的两个端点上都是错的(10 条可能是 1 个席位也可能是 2 个)。
   */
  todayCap?: number | null;

  // ── 0917 批次五(契约 §16.1 / §16.2)。**全部选填**:api 那辆车还在路上,
  //    缺席时页面按「没有」渲染(整块不出现),不报错、不编一份。
  /**
   * 从知识库读出来的获客场景(契约 §16.1)。**推荐档的新底**:
   * 被 ≥1 个场景引用的线才配当主推,一条场景都没引用的线一律 P2。
   * ★ KB 为空 ⇒ 服务端给 `[]`(不是 null 也不是缺席):那时页面要说「先传资料」,
   *   而不是把一块空的「获客场景」摆在那儿让人以为没加载出来。
   */
  scenes?: AcqScene[] | null;
  /**
   * 每个在售产品的两个数(契约 §16.2)。产品页签就是照它画的,**顺序照后端给的**
   * (产品树顺序)—— 前端不按名字或数字重排,不然两页的页签排列不一样。
   */
  productSummary?: AcqProductSummary[] | null;
}

/**
 * 一个获客场景(契约 §16.1)——「谁 · 在什么时刻 · 需要我哪个产品」。
 *
 * ★ 它是这一版推荐的**理由层**:上一版只有「007 平台优先级 + KB 升降一档」,
 *   于是知产味的全局排序照搬给了每一个行业(老板:「你是不是偷懒了」)。
 * ★ `productCode` 可以为 null(推不出挂哪个产品的场景照样是真场景),
 *   **不许编一个产品码**充数。
 * ★ `sources` 是目录里的 `type` 码 —— 场景与目录卡的连线全靠它。
 */
export interface AcqScene {
  id: string;
  title: string;
  who?: string | null;
  moment?: string | null;
  productCode?: string | null;
  /** KB 片段号(`K3` 这种),与 `why` 里写的那个号是同一套。 */
  refs?: string[] | null;
  /** 这个场景**真抓得到的**发现种类(目录 `type` 码)。 */
  sources?: string[] | null;

  // ── 0918 批次六(契约 §17.2 / §17.3.5)。选填 ──────────────────────────
  /**
   * 「材料不足,AI 猜的」(契约 §17.2):这个产品的资料是产品介绍不是问答,检索捞不回段落,
   * 模型只能给一条**候选**场景(`refs` 可为空)。
   * ★★ 它**不参与定档**(`covers` 不算它):一条猜出来的场景把一条线顶进「主推」,
   *   等于拿一个没有证据的理由去劝用户花钱。面上照显、灰着、标出来 ——
   *   显示它是为了让用户知道"这个产品我们读不出场景",而不是为了给它撑场面。
   */
  weak?: boolean | null;
  /**
   * 这个场景是从哪几篇文档读出来的(契约 §17.3.5)。`refs` 里那些 K# 的人话版本。
   * ★ 与 `refs` 有意并存:`refs` 要和 `why` 里的「(K1)」对得上(换成 docId 就对不上了),
   *   而用户要点开看的是**文档名**。
   */
  refDocs?: AcqSceneRefDoc[] | null;
}

/** 场景的出处文档(契约 §17.3.5)。`docName` 是给人看的那一格。 */
export interface AcqSceneRefDoc {
  docId: string;
  chunkId?: string | null;
  docName: string;
}

/**
 * 一个产品在获客仓里的两个数(契约 §16.2)。
 *
 * ★ `code` 为空串 = **没绑产品的那一堆**(契约原文 `{code:"", name:"未指定产品"}`)。
 *   ★★ 它在页签上**不能当一个可点的档**:`productCode=""` 发给后端的意思是"不筛",
 *   与「全部」逐字同形 —— 摆成页签的话点下去看到的是全部,而标签写着"未指定产品"。
 *   所以面上把它报成一句「另有 N 条没绑产品」,等后端给出"只看未绑定"的筛法再说。
 * ★ `openCount` = 非终态卡数(线索库口径);`todayCount` = 今日页里的数。两个数不是一回事,
 *   页签显示哪一个由**所在页**决定 —— 今日页显示今日数,别的页显示在手数。
 */
export interface AcqProductSummary {
  code: string;
  name: string;
  openCount: number;
  todayCount: number;
}

/** 1011:一条已解析的竞品目标。`input` = 用户粘贴的原文(原样回显),`id` = 平台 id。 */
export interface AcqPeerTarget {
  input: string;
  id: string;
  url?: string | null;
}

/** 1011:评论线的竞品账号 / 指定内容。 */
export interface AcqPeerTargets {
  accounts: AcqPeerTarget[];
  posts: AcqPeerTarget[];
  /** 每个竞品账号一轮读几条作品(1–5,默认 2)。 */
  accountPostsN: number;
}

/** PUT /acq/catalog/{type} 的 body。传了 `keywords` ⇒ 服务端把 `keywordsSource` 置 `user`。 */
export interface AcqCatalogItemUpdate {
  keywords?: string[];
  /** 1009 评论线(契约 §2):传了任一 ⇒ 服务端把 `keywordsSource` 置 `user`。 */
  topicWords?: string[];
  intentPatterns?: string[];
  /**
   * 1011:竞品账号 / 指定内容的**原文清单**(整张替换;缺席 = 不改,空数组 = 清空)。
   * 后端负责解析成平台 id,认不出的整批 400 并逐条说原因(页面原样把那句话弹出来)。
   */
  peerAccounts?: string[];
  peerPosts?: string[];
  peerAccountPostsN?: number;
  regions?: string[];
  subscribed?: boolean;
  cadence?: AcqCadence;
  /**
   * 0917 批次二:用户手调推荐档 ⇒ 服务端把 `levelSource` 置 `user`(契约 §13.4)。
   *
   * ★★ 0918 批次七(契约 §18.1-2):`null` = **删掉这一格**,而且**只在带 `productCode` 时才有意义**
   *   —— 它是「在「X」下不看」的恢复动作:把 `product_levels_json[code]` 那一格抹掉,
   *   这条线回到按场景重算。`null` 与 `"NA"` 不是一回事(前者是"用户没说过话"),
   *   所以恢复**不能**写 `level:"P2"` 充数:那会在页面上留下一个用户从没点过的档。
   * ★ 不带 `productCode` 发 `null` 后端会当成"没给"(全局档一个字不动)。页面侧不存在这条路
   *   —— 恢复入口只在产品页签下出现,而那条路一定带 `productCode`。
   */
  level?: AcqPrefLevel | null;
  /** 「不看这个」/「恢复」。 */
  hidden?: boolean;
  /**
   * 0918 批次六(契约 §17.2 / §17.3.1):**这一改只对这个产品生效**。
   *
   * ★ 给了 `productCode`:`level` 写进 `product_levels_json`(不动全局档)、
   *   `subscribed` 写进 `subscribed_products_json`(不动全局订阅)。
   * ★ **空串不发**(与 `runDiscover` 同一条):`productCode=""` 在后端的意思是"没给",
   *   发一个空串会把一次"按产品改档"静默变成一次改全局档 —— 页面不报错,
   *   而用户在 A 产品下的一次点击改掉了所有产品。
   */
  productCode?: string;
}

/** 周策略卡的一行(契约 §12.4 `GET /acq/strategy`)。 */
export interface AcqStrategyRow {
  keyword: string;
  avg30: number;
  last7: number;
  deltaPct: number;
}

/**
 * 周策略卡。
 * ★ `available=false` = 该行业还没铸 007 信号词典 ⇒ 没有种子词,算不出趋势。
 *   这不是故障,页面要如实说,**不许拿一份编的趋势充数**。
 */
export interface AcqStrategyView {
  available: boolean;
  industryCode?: string | null;
  weekStart?: string | null;
  weekEnd?: string | null;
  rows: AcqStrategyRow[];
  newSceneWords: string[];
}

/**
 * 契约 §4.3 → §12.3 收缩:**只剩推送设置**。
 * `keywords / regions / industryCodes / sources` 四个字段后端不再读(列还在,下个迁移清),
 * 它们的职责搬到了目录的每一行(per-type keywords / subscribed)。
 */
export interface AcqSubscription {
  keywords: string[];
  regions: string[];
  industryCodes: string[];
  sources: string[];
  /** 默认 10,**上限 10**(契约 §4.3:只留 3–10 条,不是 37 条)。 */
  dailyCap: number;
  pushEnabled: boolean;
  pushHour: number;
}

export const DAILY_CAP_MAX = 10;
export const DAILY_CAP_DEFAULT = 10;

/** 契约 §6 `attributes` **只认这五个键**。 */
export interface AcqIcpAttributes {
  industries?: string[];
  sizes?: string[];
  regions?: string[];
  roles?: string[];
  triggers?: string[];
}

/**
 * 一条 ICP / 排除项(后端 `IcpItem`)。
 *
 * ★ `factId` 是 **Long**(数字),不是字符串 —— `confirmIcp` 的入参跟着是 number[]。
 * ★ `confirmed` 是 **boolean**,不是 `status` 枚举。
 * ★ `productName` 在**租户级画像**或**产品已下架**时为 null。这两种情况语义不同,
 *   前端按 `productCode` 再分一次(见 `icpScopeLabel`)。
 * ★ `sourceRefs`:ICP 是 AI 从 KB 推的,用户点头之前得能**回原文核对** ——
 *   与商机卡事实块「看原文↗」同一条纪律(点不开出处的推断,不该请人点头)。
 */
export interface AcqIcpSourceRef {
  /** KB 文档标题 / 切块摘要,后端给什么显示什么。 */
  title?: string | null;
  docTitle?: string | null;
  snippet?: string | null;
  url?: string | null;
  sourceUrl?: string | null;
  chunkId?: string | null;
  [k: string]: unknown;
}

export interface AcqIcpItem {
  factId: number;
  kind: "icp" | "exclusion";
  /** `xb_product_node.node_code`;租户级用保留名 `__tenant__`(契约 §6)。 */
  productCode?: string | null;
  productName?: string | null;
  value: string;
  attributes?: AcqIcpAttributes | null;
  /** 0~1。队列按它降序 —— AI 越没把握的越该排在后面等人看。 */
  confidence?: number | null;
  sourceRefs?: AcqIcpSourceRef[] | null;
  confirmed: boolean;
  /**
   * 这条是谁提的(契约 §16.6;api 侧 `source_channel=manual` ⇒ `user`)。
   *
   * ★★ 它回答的是这一页最容易认错的那件事:**「公司 注册」这种一个词的画像是用户自己加的**,
   *   而旁边那条整句话的是 AI 从 KB 推的。不分来源的话,用户看到自己加的词写着
   *   「AI 推的」会不信整页;看到 AI 推的以为是自己加的就不会去核原文。
   * ★ **缺席 ⇒ 小标整个不出现**(后端还没跟上),不回落成「AI 推的」——
   *   回落一个默认值等于替一条不知来源的事实作证。
   */
  source?: "ai" | "user";
}

/**
 * 后端 `IcpPage(List<IcpItem> icps, List<IcpItem> exclusions, int pendingCount, int kbDocCount)`。
 *
 * ★★ `kbDocCount` 必须用上:实测 33 个租户里**绝大多数 KB=0**,
 *   所以「推不出东西」是**常态不是故障**。KB 为 0 时页面要明说「先传资料再来」,
 *   不能显示空白、更不能说「没推出来,再试一次」—— 后者会让用户以为功能坏了,
 *   然后一遍遍点那个永远不会成功的按钮。
 */
export interface AcqIcpPage {
  icps: AcqIcpItem[];
  exclusions: AcqIcpItem[];
  pendingCount: number;
  kbDocCount: number;
  /**
   * 上次推断的时刻(契约 §14.6:取 pending/approved 事实里最新的 `created_at`)。
   * ★ 它回答的是这一页唯一会随时间变坏的那件事:**画像是照着半年前的资料推的**。
   *   没有这个时刻,一份过期画像和一份刚推的长得一模一样,而 `fit` 分天天照着它打。
   * ★ 缺席(后端没跟上 / 一条事实都还没有)⇒ 整句不说,**不写「未知」**。
   */
  lastSuggestedAt?: string | null;
  /**
   * 这一次**没动**的条数(只有 `POST /icp/reject` 给数字)。
   *
   * ★★ `null` = **这一口根本没算这个数**,不是 0(page / suggest / propose / confirm 恒 null)。
   *   把 null 读成 0 的后果不是崩,是在那四口上说出「0 条没动」这种它没资格说的话 ——
   *   一个从来没统计过的量被当成"统计出来是零"。所以面上判 `typeof x === "number"`,
   *   不判真值也不 `?? 0`。
   */
  skipped?: number | null;
  /**
   * 本租户**被驳回过**的事实(五个口都带)。场景词 chip 的第三态就靠它。
   *
   * ★★ 为什么要把"被删掉的东西"回传:不回传的话,用户忽略掉的词**下周照样原样冒出来**
   *   (场景词来自行业趋势,趋势不会因为他点过 ✗ 就不再出这个词)—— 他于是每周都要
   *   再点一次同一个 ✗,而系统看起来像是没记住他说过的话。
   * ★ 只带 `value / productCode / kind` 三样:这一格是拿来**比对**的,不是拿来展示的,
   *   带整条事实(属性、出处、把握)只会让这一口跟着 IcpItem 的字段一起改。
   */
  rejected?: AcqIcpRejectedRef[] | null;
}

/**
 * 一条被驳回过的事实的**比对键**。
 * ★ `productCode` 缺省 = 租户级(`__tenant__`)—— 与 `AcqIcpItem.productCode` 同一套写法。
 */
export interface AcqIcpRejectedRef {
  value: string;
  productCode?: string | null;
  kind: "icp" | "exclusion";
}

/** `POST /acq/icp/propose` 的 body(契约 §14.6)。 */
export interface AcqIcpProposal {
  kind: "icp" | "exclusion";
  value: string;
  /** 挂到哪个产品下;不传 = 租户级(服务端落 `__tenant__`)。 */
  productCode?: string;
}

/**
 * 「未指定产品」这一档的**筛选哨兵**(契约 §16.2 api 落地形状,0918)。
 *
 * ★★ `productSummary` 里那一行的 `code` 是**空串**(契约逐字,它是给人看的),
 *   但查询串里 `productCode=` 与**不传**是同一件事(= 不筛)——
 *   照原样把空串发过去,后端会把「未指定产品」渲染成**全部卡**:
 *   页面不报错、数字对不上、没人会当成 bug。
 * ★ 所以三仓约定:**显示用空串,筛选发 `__none__`**,这一次映射由 console 做
 *   (`/today` 与 `/opportunities` 两个口都认这个哨兵)。同 `assigneeId=0`(未分派)那条。
 * ★ URL 里存的也是 `__none__` —— 它得能分享、能从今日跳到线索库还是同一档,
 *   而空串在 URL 里等于"没选"。
 */
export const ACQ_PRODUCT_NONE = "__none__";

/** 租户级 ICP 的保留产品名(契约 §6)。 */
export const ICP_TENANT_SCOPE = "__tenant__";

/**
 * 一条 ICP 管的是哪个产品。
 *
 * ★ `productName` 为 null 有**两个**原因(租户级画像 / 产品已下架),它们不是一回事:
 *   把「某个已下架产品的画像」显示成「不指定产品」,等于悄悄把一条窄画像的适用面放大,
 *   而用户点头时以为自己批的是全公司口径。所以这里再按 `productCode` 分一次 ——
 *   有码且不是保留名 ⇒ 是个具体产品,只是名字查不到了,如实说「已下架」。
 */
export function icpScopeLabel(item: Pick<AcqIcpItem, "productCode" | "productName">): string {
  if (item.productName) return item.productName;
  const code = item.productCode?.trim();
  // 0917 批次四(契约 §15.3):`__tenant__` 一律「不指定产品」 —— 「全公司」听起来像
  // "公司整体战略",而它的意思只是**这条没挂在任何一条产品线上**。
  if (!code || code === ICP_TENANT_SCOPE) return "不指定产品";
  return `${code}（已下架）`;
}

/**
 * ICP 队列按 `confidence` 降序 —— **AI 越没把握的越该排在后面等人亲自看**。
 *
 * ★ 顺序目前由**前端**保证:后端 `allAcqFacts` 是 pending / approved 两轮拼出来的,
 *   顺序不保证(`xb_company_fact` 上有 `idx_company_fact_review(tenant_code, review_status,
 *   confidence)`,将来后端真排了也是顺的)。
 * ★ 这一行是**幂等**的:后端哪天开始排了,这里再排一次结果不变,两边不会打架。
 * ★ 没给 `confidence` 的排在最后(不知道把握 = 比低把握更该看一眼);
 *   全都没给时它们的排序键相等,而 `Array#sort` 是稳定的 ⇒ **原顺序原样保留**,
 *   不会因为加了这行就把一份本来有意义的服务端顺序打乱。
 */
export function byConfidenceDesc(a: AcqIcpItem, b: AcqIcpItem): number {
  return (b.confidence ?? -1) - (a.confidence ?? -1);
}

/** ICP 的一条出处能不能点开。取不到链接的不渲染成链接(与事实块同则)。 */
export function icpRefUrl(ref: AcqIcpSourceRef): string | null {
  return ref.sourceUrl || ref.url || null;
}

export function icpRefTitle(ref: AcqIcpSourceRef): string {
  return ref.docTitle || ref.title || ref.snippet || ref.chunkId || "知识库原文";
}

// ── dev 桩:后端没上线时让页面跑起来 ────────────────────────────────────────
//
// ★ 判据用 MODE 不用 DEV:vitest 下 `import.meta.env.DEV` 也是 true,那样组件测会
//   悄悄吃到桩数据 —— 失败形态是静默的(测试永远绿,因为它测的是桩)。
//   MODE 在 `vite dev` 下是 development、vitest 下是 test、构建是 production。
const FIXTURES_ON = import.meta.env.MODE === "development";

/**
 * ★★ 回落到桩的那一刻必须**在页面上说出来**(0916 纪律:fixture 开关 + 明标「样例数据」,
 *   不做静默回落)。
 *
 *   为什么:静默回落最坏的形态不是"数据是假的",是**一个坏掉的后端在页面上看起来是好的**
 *   —— 联调时对着一屏编出来的卡讨论排版,而真正该红的那口从头到尾没人看见。
 *   所以这里留一个进程内的旗子 + 订阅口,`AcqShell` 在顶上挂一条「样例数据」横幅。
 * ★ 旗子只会从 false → true(这一次会话里已经骗过一次人,后面就一直标着),
 *   刷新页面即清 —— 不做"某口成功了就撤旗",那会让横幅忽隐忽现、更像是页面自己有毛病。
 */
let fixtureUsed = false;
const fixtureListeners = new Set<() => void>();

export function isAcqFixtureData(): boolean {
  return fixtureUsed;
}

export function subscribeAcqFixtureFlag(fn: () => void): () => void {
  fixtureListeners.add(fn);
  return () => {
    fixtureListeners.delete(fn);
  };
}

/** 测试/走查用:把旗子放倒。 */
export function __resetAcqFixtureFlag() {
  fixtureUsed = false;
  fixtureListeners.forEach((fn) => fn());
}

function markFixtureUsed() {
  if (fixtureUsed) return;
  fixtureUsed = true;
  fixtureListeners.forEach((fn) => fn());
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 「列表里那份卡已经旧了」的信号(契约 §14.9 重判补绑)
 * ═══════════════════════════════════════════════════════════════════════════
 * ★ 重判在**我的生意**页触发,改的却是今日与线索库里那些卡的产品格与意向格。
 *   这里发一个订阅式的版本号,两页把它读进各自的 query 依赖 —— 挂着的那一页当场重取。
 * ★★ 说清楚它今天能做到什么:今日 / 线索库**本来就在 mount 时重取**(这一舱没走
 *   react-query,没有跨页缓存),所以多数时候用户走过去看到的已经是新的。这个信号
 *   补的是"两页同时挂着"以及将来真有缓存时的那一格 —— 它是个**真信号**,
 *   不是一句什么都不做的 `invalidateQueries`(那种调用会让下一个人以为这里有缓存)。
 */
let listsRevision = 0;
const listsListeners = new Set<() => void>();

export function markAcqListsStale() {
  listsRevision += 1;
  listsListeners.forEach((fn) => fn());
}

export function subscribeAcqLists(fn: () => void): () => void {
  listsListeners.add(fn);
  return () => {
    listsListeners.delete(fn);
  };
}

export function acqListsRevision(): number {
  return listsRevision;
}

async function withFixture<T>(run: () => Promise<T>, pick: (m: AcqFixtures) => T): Promise<T> {
  try {
    return await run();
  } catch (e) {
    if (!FIXTURES_ON) throw e;
    const mod = await import("./acqFixtures");
    logger.warn("[acq] 后端未就绪,本次走 dev 本地桩", { error: String(e) });
    markFixtureUsed();
    return pick(mod);
  }
}

type AcqFixtures = typeof import("./acqFixtures");

function qs(q: AcqOpportunityQuery): string {
  const p = new URLSearchParams();
  p.set("page", String(q.page ?? 1));
  p.set("pageSize", String(q.pageSize ?? 20));
  if (q.state) p.set("state", q.state);
  if (q.source) p.set("source", q.source);
  if (q.feedbackCode) p.set("feedbackCode", q.feedbackCode);
  // ★ `!= null` 不是 `if (q.assigneeId)`:未分派传的就是 **0**,真值判断会把它整个吃掉
  //   ——「未分派」那一档于是变成"不筛",页面显示全部行而没有任何一处报错。
  if (q.assigneeId != null) p.set("assigneeId", String(q.assigneeId));
  if (q.productCode) p.set("productCode", q.productCode);
  if (q.intentType) p.set("intentType", q.intentType);
  return p.toString();
}

/** 今日商机的 query(契约 §13.4)。`mine` 只在 true 时才发 —— `mine=false` 与不传是一回事。 */
function todayQs(q: AcqTodayQuery): string {
  const p = new URLSearchParams();
  p.set("page", String(q.page ?? 1));
  p.set("size", String(q.size ?? TODAY_PAGE_SIZE));
  if (q.mine) p.set("mine", "true");
  if (q.scope) p.set("scope", q.scope);
  if (q.productCode) p.set("productCode", q.productCode);
  if (q.intentType) p.set("intentType", q.intentType);
  return p.toString();
}

/** 今日商机每页条数(契约 §13.1:今日页分页,每页 20)。 */
export const TODAY_PAGE_SIZE = 20;

export const acqService = {
  /**
   * 今日商机(默认落地页)。按 priorityScore 降序 —— 排序由服务端定,前端不复排。
   * ★ 0917 批次二:分页 + `mine`(只看分派给我的)+ `productCode`(按产品筛),契约 §13.4。
   */
  today: (q: AcqTodayQuery = {}) =>
    withFixture(
      () => api.get<AcqTodayView>(`${BASE}/today?${todayQs(q)}`),
      (m) => m.fixtureToday(q)
    ),

  // ── 发现目录(契约 §12.4) ──────────────────────────────────────────────
  /** 目录 + 本租户偏好 + 价。这一舱的入口。 */
  catalog: () =>
    withFixture(
      () => api.get<AcqCatalogView>(`${BASE}/catalog`),
      (m) => m.fixtureCatalog()
    ),

  /**
   * 按知识库 + 行业包重新推断推荐档(**免费**)。
   * ★ 用户手改过词的行只更新 level/why,**不覆盖词**(契约 §12.10,服务端保证)。
   */
  /**
   * ★ 0918 批次六(契约 §17.2):`productCode` **选填** —— 给了就**只重推这个产品**的场景与
   *   它的词组,别的产品的场景**不动**(后端合并而不是覆盖)。零场景那个产品的兜底按钮走这一口。
   * ★ 空串不发(同 `runDiscover`):空串 = 没给 = 整租户重推,而用户按的是
   *   「让 AI 只为这个产品再推一次」。
   */
  recommend: (productCode?: string) =>
    withFixture(
      () =>
        api.post<AcqCatalogView>(`${BASE}/catalog/recommend`, productCode ? { productCode } : {}),
      (m) => m.fixtureRecommend(productCode)
    ),

  /** 改一种的关键词/地区/订阅/节奏。返回**那一行**,前端就地替换,不重拉整页。 */
  updateCatalogItem: (type: string, patch: AcqCatalogItemUpdate) =>
    withFixture(
      () => api.put<AcqCatalogItem>(`${BASE}/catalog/${type}`, patch),
      (m) => m.fixtureUpdateCatalogItem(type, patch)
    ),

  /**
   * 发起一轮采集(**按种类收费**)。0927 起**异步**:立即回 `{runId, RUNNING}`,结果靠 `discoverRun` 轮询。
   *   此前是同步等 3~6 分钟,演示机 nginx 180s / 生产 300s 先掐成 504 —— 用户看到一页 HTML 报错,后台其实还在跑。
   * ★★ 不走 withFixture 的「失败就回桩」:这是**会扣积分**的写动作。
   *   假装跑过了 = 对一个用户说"扣了 180 积分、没找到" —— 而其实什么也没发生。
   *   别的口回桩只是数据是编的,这一口回桩是**账是编的**,性质不同。
   * ★ body `{type}` 必填,旧的无 body 调用后端 400(契约 §12.4)。
   * ★ 0917 批次五(契约 §16.2):`productCode` **选填** —— 给了就只用这个产品的词组跑,
   *   落卡时没绑产品的默认写它。**空串不发**:`productCode=""` 在后端的意思是"没给",
   *   发一个空串只会在日志里多一条看着像"按产品跑了"的假记录。
   */
  runDiscover: (type: string, productCode?: string) =>
    api.post<AcqDiscoverRunStarted>(`${BASE}/discover/run`, {
      type,
      ...(productCode ? { productCode } : {}),
    }),

  /**
   * 读一轮的状态(0927 异步化)。不是本租户的号 / 过期的号 → 404。
   * ★ 同样不回桩:它读的是一次真扣费动作的结果,桩出来的「成功」等于编一笔账。
   */
  discoverRun: (runId: string) =>
    api.get<AcqDiscoverRunView>(`${BASE}/discover/runs/${encodeURIComponent(runId)}`),

  /** 周策略卡(行业级缓存)。没铸 007 种子词 → `available=false`。 */
  strategy: () =>
    withFixture(
      () => api.get<AcqStrategyView>(`${BASE}/strategy`),
      (m) => m.fixtureStrategy()
    ),

  /**
   * 接手一条(**免费**)。ai 出产物 → 落 `deliverable`,state 按动作推进。
   * ★ 免费所以可以回桩:回桩最坏的后果是"产物是编的",而不是"账是编的"。
   */
  take: (id: string) =>
    withFixture(
      () => api.post<AcqTakeResult>(`${BASE}/opportunities/${id}/take`, {}),
      (m) => m.fixtureTake(id)
    ),

  /**
   * 按现在的产品树把老卡重判一遍(契约 §14.9)。**TENANT_ADMIN 才调得动,门在服务端。**
   *
   * ★ `onlyUnbound=true`(默认)= 只补没绑上产品 / 没有意向的那些;`false` 是产品树改过
   *   之后的全量重判。前端默认只发 true —— 全量重判是一次几百张卡的 LLM 活,
   *   不该由一个看起来无害的按钮顺手触发。
   * ★ 免费所以可以回桩(与 take 同则):回桩最坏的后果是"数字是编的",不是"账是编的"。
   * ★ 成功之后要 `markAcqListsStale()`:今日与线索库手上那份卡的产品格已经旧了。
   */
  rejudgeOpportunities: (onlyUnbound = true) =>
    withFixture(
      () => api.post<AcqRejudgeResult>(`${BASE}/opportunities/rejudge`, { onlyUnbound }),
      (m) => m.fixtureRejudge()
    ),

  opportunities: (q: AcqOpportunityQuery = {}) =>
    withFixture(
      () => api.get<AcqOpportunityPage>(`${BASE}/opportunities?${qs(q)}`),
      (m) => m.fixtureOpportunities(q)
    ),

  detail: (id: string) =>
    withFixture(
      () => api.get<AcqOpportunityCard>(`${BASE}/opportunities/${id}`),
      (m) => m.fixtureDetail(id)
    ),

  /**
   * **补全联系方式**(契约 §12.4;端点路径不变,语义从「解锁脱敏字段」改成「去公网查一次」)。
   * ★ 查不到 → `found=false, charged=false`,**不扣分**;重复点 → `charged=false` 幂等。
   *   两种都不扣,但要说的话完全不同,所以返回体里两格都得读。
   */
  unlock: (id: string) =>
    withFixture(
      () => api.post<AcqUnlockResult>(`${BASE}/opportunities/${id}/unlock`, {}),
      (m) => m.fixtureUnlock(id)
    ),

  /**
   * **批量补全联系方式**(1015,≤ 50 条)。后台逐条走单条补全,查到才扣;余额不足即停。
   * 返回首个进度快照,拿 `taskId` 轮询 `batchUnlockProgress`。
   */
  batchUnlock: (ids: string[]) =>
    withFixture(
      () =>
        api.post<AcqBatchUnlockProgress>(`${BASE}/opportunities/batch-unlock`, {
          ids: ids.map((x) => Number(x)),
        }),
      (m) => m.fixtureBatchUnlock(ids)
    ),

  /** 企业库找客户:页面初始化(行业表 / 标签 / 价 / 余量 / 画像预填)。 */
  entOptions: () =>
    withFixture(
      () => api.get<AcqEntOptions>(`${BASE}/ent/options`),
      (m) => m.fixtureEntOptions()
    ),

  /** 搜一页(按页扣;0 条不扣;24h 内重看同一页不扣)。 */
  entSearch: (req: AcqEntSearchRequest) =>
    withFixture(
      () => api.post<AcqEntSearchResult>(`${BASE}/ent/search`, req),
      (m) => m.fixtureEntSearch(req)
    ),

  /** 名称联想(免费)。 */
  entSuggest: (word: string) =>
    withFixture(
      () => api.get<AcqEntSuggestion[]>(`${BASE}/ent/suggest?word=${encodeURIComponent(word)}`),
      (m) => m.fixtureEntSuggest(word)
    ),

  /** 勾选入库(免费)。返回这批线索的 id,接着可以批量补全。 */
  entImport: (searchId: string, creditCodes: string[], productCode?: string | null) =>
    withFixture(
      () =>
        api.post<AcqEntImportResult>(`${BASE}/ent/import`, {
          searchId,
          creditCodes,
          productCode: productCode || null,
        }),
      (m) => m.fixtureEntImport(creditCodes)
    ),

  batchUnlockProgress: (taskId: string) =>
    withFixture(
      () => api.get<AcqBatchUnlockProgress>(`${BASE}/opportunities/batch-unlock/${taskId}`),
      (m) => m.fixtureBatchUnlockProgress(taskId)
    ),

  setState: (id: string, state: AcqState) =>
    withFixture(
      () => api.post<AcqOpportunityCard>(`${BASE}/opportunities/${id}/state`, { state }),
      (m) => m.fixtureSetState(id, state)
    ),

  /**
   * 分派给某人(契约 §13.4)。**TENANT_ADMIN / TENANT_MANAGER 才有这口**,坐席走 `claim`。
   * ★ `assigneeId=null` = **取消分派**(不是"分派给 null"):所以参数显式收 `number | null`,
   *   调用方不许用 `undefined` 表示"取消" —— JSON 里 undefined 会整个键消失,
   *   后端读到的是"这次没提分派",于是取消动作静默不生效。
   * ★ 门在服务端。前端按角色藏按钮只是不给人点一个必然 403 的东西,**不是权限本身**。
   */
  assign: (id: string, assigneeId: number | null) =>
    withFixture(
      () => api.post<AcqOpportunityCard>(`${BASE}/opportunities/${id}/assign`, { assigneeId }),
      (m) => m.fixtureAssign(id, assigneeId)
    ),

  /** 坐席认领(契约 §13.4)。**只有未分派的能认**;已分派给别人后端回 409。 */
  claim: (id: string) =>
    withFixture(
      () => api.post<AcqOpportunityCard>(`${BASE}/opportunities/${id}/claim`, {}),
      (m) => m.fixtureClaim(id)
    ),

  // ★ `tenantUsers`(GET /tenant/users)0917 批次四退役(契约 §15.3):选人换成系统那一个
  //   `MemberTreePicker`,人名由它从组织树 + 花名册(`/tenant/members`)自己取。
  //   留着这一口的后果是两份人名表并存,而它们的过滤规则(停用 / 在不在部门里)并不一样。

  /**
   * 本租户在售产品(产品筛子 / 「我卖什么」/ 关键词分组的名字表)。
   * ★ 复用 entity 的 `productService.list()`(GET `/tenant/products`),不在这儿再抄一遍端点。
   * ★ 口径是 **status=1 的全部节点(category 也算)**:精判与推荐是从后端 listLive(全部存活节点)
   *   里选产品码的,中细软那 10 个全是 category —— 只留 type=product 的话名字表是空的,
   *   关键词分组标题就会显示成 `hitech_cert:` 这种 node_code(0917 联调真机抓到,不报错)。
   *   与 `pickableProducts`(投给客户的素材选择器,还要滤掉 internal)**不是一回事** ——
   *   一个 internal 的在售产品照样是我们卖的东西,筛商机时不该看不见它。
   */
  tenantProducts: () =>
    withFixture(
      async () => {
        const nodes = await productService.list();
        return nodes
          .filter((n) => n.status === 1)
          .map((n) => ({ nodeCode: n.code, name: n.name, summary: n.summary ?? null }));
      },
      (m) => m.fixtureTenantProducts()
    ),

  feedback: (id: string, feedbackCode: AcqFeedbackCode, note?: string) =>
    withFixture(
      () =>
        api.post<AcqOpportunityCard>(`${BASE}/opportunities/${id}/feedback`, {
          feedbackCode,
          ...(note ? { note } : {}),
        }),
      (m) => m.fixtureFeedback(id, feedbackCode)
    ),

  /** 我在盯什么。没配过返回 null(前端据此走「只配一次」的首配流)。 */
  subscription: () =>
    withFixture(
      () => api.get<AcqSubscription | null>(`${BASE}/subscriptions`),
      (m) => m.fixtureSubscription()
    ),

  /** 整体覆盖,不做增量(契约 §4)。 */
  saveSubscription: (sub: AcqSubscription) =>
    withFixture(
      () => api.put<AcqSubscription>(`${BASE}/subscriptions`, sub),
      (m) => m.fixtureSaveSubscription(sub)
    ),

  // ★ `suggestSubscription`(POST /subscriptions/suggest)0917 v2 退役:
  //   「AI 从 KB 推一套订阅词」这件事搬到了 `recommend()` —— 它推的不再是一张
  //   大而全的词表,而是**每一种发现各自的档位、理由和词**。留着一个会 404 的方法
  //   只会让下一个人以为那条路还在。

  icp: () =>
    withFixture(
      () => api.get<AcqIcpPage>(`${BASE}/icp`),
      (m) => m.fixtureIcp()
    ),

  suggestIcp: () =>
    withFixture(
      () => api.post<AcqIcpPage>(`${BASE}/icp/suggest`, {}),
      (m) => m.fixtureSuggestIcp()
    ),

  /**
   * 用户从场景词一键提名一条画像 / 排除项(契约 §14.6,**免费**)。
   *
   * ★★ 提名**不等于生效**:服务端走 `facts.propose` 建的是一条 **pending** 事实,
   *   还得在这一页点「确认」才进打分。面上的话必须说清这一步(「已提名,去确认」),
   *   否则用户以为加完就生效了,而 `fit` 分一点没变。
   * ★ 免费所以可以回桩 —— 回桩最坏的后果是"多了一条编的待确认",不是"账是编的"。
   */
  icpPropose: (kind: AcqIcpProposal["kind"], value: string, productCode?: string) => {
    /**
     * ★★ **发送前 trim**:api 侧 V46 的唯一键是 `sha256(trim(value))`(契约 §15.2)。
     *   前端不 trim 的后果不是报错 —— 是「商标起名 」和「商标起名」在库里**归一成同一条**
     *   (后端 trim 过了)、而前端按原串比对 ⇒ 面上那枚 chip 永远不变「已提名 ✓」,
     *   用户于是一遍遍重提同一个词,每次都以为没加上。两端的归一规则必须逐字一致。
     */
    const v = value.trim();
    return withFixture(
      () =>
        api.post<AcqIcpPage>(`${BASE}/icp/propose`, {
          kind,
          value: v,
          ...(productCode ? { productCode } : {}),
        }),
      (m) => m.fixtureIcpPropose(kind, v, productCode)
    );
  },

  /** ★ factIds 是 **number[]**(后端 `IcpItem.factId` 是 Long)。 */
  confirmIcp: (factIds: number[]) =>
    withFixture(
      () => api.post<AcqIcpPage>(`${BASE}/icp/confirm`, { factIds }),
      (m) => m.fixtureConfirmIcp(factIds)
    ),

  /**
   * 「这条我不要」(`POST /acq/icp/reject`,与 confirm 同形)。
   *
   * ★★ 上一版待确认托盘上**只有 ✓ 没有 ✗**,因为后端那时没有这一口。
   *   一个只能说"是"的待办列表,用户清不掉它 —— 剩下的那几条会一直挂在那儿,
   *   最后整条托盘被当成装饰无视掉,而它本来是这一页唯一的待办。
   * ★ 非 pending 的 id 服务端**忽略**(已确认过的那条不会被一枚误点的 ✗ 打回),
   *   忽略了几条从 `skipped` 回来 —— 面上要照实说,不能报"已忽略 N 条"却其实只动了 N-2 条。
   * ★ 返回**与 confirm 同形**(整页 IcpPage),只有 `skipped` 这一格是数字;
   *   其余四口那一格恒 `null`(= 没算过,不是 0)。
   */
  rejectIcp: (factIds: number[]) =>
    withFixture<AcqIcpPage>(
      () => api.post<AcqIcpPage>(`${BASE}/icp/reject`, { factIds }),
      (m) => m.fixtureRejectIcp(factIds)
    ),
};

/**
 * 能点开原文的事实才算事实(契约 §4.1 硬要求①)。
 * ★ 缺 `evidenceId` / `sourceUrl` 的整条丢掉,**不降级成"无来源"灰条** ——
 *   一条看起来像事实、却点不到出处的句子,比不出现坏得多。
 */
export function citableFacts(card: Pick<AcqOpportunityCard, "facts">): AcqFact[] {
  return (card.facts ?? []).filter((f) => !!f && !!f.evidenceId && !!f.sourceUrl && !!f.text);
}
